/**
 * Service for `/get-ready/announcements/` — backed by
 * `GetReadyAnnouncementViewSet` on the backend (full CRUD).
 *
 * Wraps `BaseService` so it composes with `createQueryHooks`, then
 * overrides each method to run rows through `GetReadyAnnouncementMapper`.
 * Mirrors `src/lib/api/garage-register/garage-register.ts` 1:1.
 *
 * Query params accepted by the backend:
 *   - `dealership_id` — tenant scope (the selected dealership id)
 *   - `is_live`       — `true` (default) returns active announcements,
 *                       `false` returns expired (Archive view)
 *   - `ordering`      — DRF ordering field name
 *   - `page` / `page_size`
 *
 * Note: backend's `perform_create` reads `dealership_id` from the query
 * string and stamps the FK server-side, so create/update payloads omit
 * the dealership FK entirely.
 */
import { AxiosRequestConfig } from "axios";

import { BaseService, ServerPaginatedResponse } from "@/lib/api/base";
import { GetReadyAnnouncementMapper } from "@/lib/adapters/getready/announcements";
import type {
  GetReadyAnnouncement,
  GetReadyAnnouncementCreateDto,
  GetReadyAnnouncementCreateServerDto,
  GetReadyAnnouncementServerDto,
  GetReadyAnnouncementUpdateDto,
  GetReadyAnnouncementUpdateServerDto,
} from "@/lib/types/getready/announcements";

export interface GetReadyAnnouncementQueryParams {
  dealership_id?: string | number;
  is_live?: boolean;
  ordering?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

const baseService = new BaseService<
  GetReadyAnnouncementServerDto,
  GetReadyAnnouncementCreateServerDto,
  GetReadyAnnouncementUpdateServerDto
>("/get-ready/announcements/");

export const getReadyAnnouncementsService = {
  getAll: async (
    params: GetReadyAnnouncementQueryParams = {},
    config?: AxiosRequestConfig
  ): Promise<ServerPaginatedResponse<GetReadyAnnouncement>> => {
    const serverData = await baseService.getAll(params, config);

    if (
      serverData &&
      typeof serverData === "object" &&
      "results" in (serverData as object) &&
      "count" in (serverData as object)
    ) {
      const paged = serverData as unknown as ServerPaginatedResponse<GetReadyAnnouncementServerDto>;
      return {
        count: paged.count,
        next: paged.next,
        previous: paged.previous,
        results: paged.results.map(GetReadyAnnouncementMapper.fromResponse),
      };
    }

    // Bare-array fallback (small result sets / pagination disabled).
    const results = Array.isArray(serverData)
      ? (serverData as GetReadyAnnouncementServerDto[])
      : [];
    return {
      count: results.length,
      next: null,
      previous: null,
      results: results.map(GetReadyAnnouncementMapper.fromResponse),
    };
  },

  getById: async (
    id: string,
    config?: AxiosRequestConfig
  ): Promise<GetReadyAnnouncement> => {
    const serverData = await baseService.getById(id, config);
    return GetReadyAnnouncementMapper.fromResponse(serverData);
  },

  create: async (
    data: GetReadyAnnouncementCreateDto,
    config?: AxiosRequestConfig
  ): Promise<GetReadyAnnouncement> => {
    // Backend reads `dealership_id` off the query string in
    // `perform_create` and stamps the FK server-side, so we just pass
    // the body through the mapper. Caller must include
    // `{ params: { dealership_id } }` in the axios config.
    const payload = GetReadyAnnouncementMapper.toCreateDto(data);
    const serverData = await baseService.create(payload, config);
    return GetReadyAnnouncementMapper.fromResponse(serverData);
  },

  update: async (
    id: string,
    data: GetReadyAnnouncementUpdateDto,
    config?: AxiosRequestConfig
  ): Promise<GetReadyAnnouncement> => {
    const payload = GetReadyAnnouncementMapper.toUpdateDto(data);
    const serverData = await baseService.update(id, payload, config);
    return GetReadyAnnouncementMapper.fromResponse(serverData);
  },

  delete: async (id: string, config?: AxiosRequestConfig): Promise<void> =>
    baseService.delete(id, config),
} as unknown as BaseService<
  GetReadyAnnouncement,
  GetReadyAnnouncementCreateDto,
  GetReadyAnnouncementUpdateDto
>;
